import { useMemo } from "react";
import classNames from "classnames";
import {
  ArrayPath,
  Control,
  FieldArray,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import { ColumnDef } from "@tanstack/react-table";
import styles from "./EditableTable.module.scss";
import Header from "../../../components/Header/Header";
import Table from "../../../../../ui/Table/Table";
import Button from "../../../../../ui/Button/Button";
import { Plus } from "../../../../../ui/Icon/Icon";
import EditableTableContext, {
  EditableTableContextValue,
} from "./EditableTableContext";

function EditableTable<T extends object>({
  propertyName,
  title,
  description,
  resourceName,
  columns,
  onSubmit,
  defaultValue,
  maxRows,
}: {
  propertyName: ArrayPath<T>;
  title: string;
  description: string;
  resourceName: {
    plural: string;
    singular: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<any, any>[];
  onSubmit: (data: T) => Promise<void>;
  defaultValue: FieldArray<T, ArrayPath<T>>;
  maxRows?: number;
}) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isDirty },
  } = useFormContext<T>();

  const rows = watch(propertyName);
  const disabled = !!maxRows && rows.length >= maxRows;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <Header
        title={title}
        description={description}
        onCancel={() => reset()}
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        isDisabled={disabled}
      />
      <EmbeddedEditableTable
        control={control}
        propertyName={propertyName}
        resourceName={resourceName}
        columns={columns}
        defaultValue={defaultValue}
        maxRows={maxRows}
      />
    </form>
  );
}

export function EmbeddedEditableTable<T extends object>({
  className,
  control,
  propertyName,
  resourceName,
  columns,
  defaultValue,
  maxRows,
  disabled,
}: {
  className?: string;
  control: Control<T>;
  propertyName: ArrayPath<T>;
  resourceName: {
    plural: string;
    singular: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<any, any>[];
  defaultValue: FieldArray<T, ArrayPath<T>>;
  maxRows?: number;
  disabled?: boolean;
}) {
  const { watch } = useFormContext<T>();

  const rows = watch(propertyName);
  const isDisabled = disabled || (!!maxRows && rows.length >= maxRows);

  const featuresMethods = useFieldArray({
    control,
    name: propertyName,
  });

  const contextValue: EditableTableContextValue<T> = useMemo(
    () => ({
      methods: featuresMethods,
      name: propertyName,
      disabled: isDisabled,
    }),
    [isDisabled, featuresMethods, propertyName],
  );

  return (
    <EditableTableContext.Provider value={contextValue}>
      <div className={classNames(styles.tableContainer, className)}>
        <Table
          showBorder
          columns={columns}
          data={featuresMethods.fields}
          resourceName={resourceName}
          Footer={
            <div className={styles.footer}>
              <Button
                leftIcon={<Plus />}
                onClick={() => featuresMethods.append(defaultValue)}
                variant="outline"
                size="small"
                disabled={disabled}
              >
                New {resourceName.singular}
              </Button>
            </div>
          }
        />
      </div>
    </EditableTableContext.Provider>
  );
}

export default EditableTable;
